import React, { useEffect, useState } from 'react';
import { ToastStack, CommandPalette, Sidebar, Kbd } from 'nelitomorphism';
import { useGitHub } from './useGitHub';
import './App.css';
import Hero from './components/Hero';
import About from './components/About';
import Experience from './components/Experience';
import Skills from './components/Skills';
import Education from './components/Education';
import Activity from './components/Activity';
import Contact, { EMAIL } from './components/Contact';
import Footer from './components/Footer';
import WebMcpTools from './components/WebMcpTools';

const SECTIONS = [
  'overview',
  'about',
  'experience',
  'skills',
  'education',
  'activity',
  'contact',
];

const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
const open = url => window.open(url, '_blank', 'noopener');

const GITHUB_URL = 'https://github.com/nelitow';
const LINKEDIN_URL = 'https://www.linkedin.com/in/nelito-jr-76b349240/';

function App() {
  const github = useGitHub();
  const [active, setActive] = useState('overview');

  // Drive the sidebar marker from whichever section is nearest the top of the
  // viewport, so the rail tracks manual scrolling as well as its own clicks.
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const items = [
    { section: 'Profile' },
    { id: 'overview', label: 'Overview', icon: '◆' },
    { id: 'about', label: 'About', icon: '≡' },
    { id: 'experience', label: 'Experience', icon: '▤' },
    { id: 'skills', label: 'Stack', icon: '◫' },
    { id: 'education', label: 'Education', icon: '▣' },
    {
      id: 'activity',
      label: 'Activity',
      icon: '◈',
      badge: { label: 'LIVE', tone: 'ok' },
    },
    { section: 'GitHub' },
    { id: 'repos', label: 'Repositories', icon: '▲', count: github.profile?.repos },
    { id: 'followers', label: 'Followers', icon: '◇', count: github.profile?.followers },
    { section: 'Contact' },
    { id: 'contact', label: 'Contact', icon: '✦' },
    { id: 'email', label: 'Email', icon: '✉' },
    { id: 'github', label: 'GitHub', icon: '↗' },
    { id: 'linkedin', label: 'LinkedIn', icon: '↗' },
  ];

  const handleNav = id => {
    if (id === 'github' || id === 'repos' || id === 'followers') return open(GITHUB_URL);
    if (id === 'linkedin') return open(LINKEDIN_URL);
    if (id === 'email') {
      window.location.href = `mailto:${EMAIL}`;
      return;
    }
    setActive(id);
    scrollTo(id);
  };

  const commands = [
    { group: 'nav', label: 'Go to Overview', action: () => handleNav('overview') },
    { group: 'nav', label: 'Go to About', action: () => handleNav('about') },
    { group: 'nav', label: 'Go to Experience', action: () => handleNav('experience') },
    { group: 'nav', label: 'Go to Stack', action: () => handleNav('skills') },
    { group: 'nav', label: 'Go to Education', action: () => handleNav('education') },
    { group: 'nav', label: 'Go to Activity', action: () => handleNav('activity') },
    { group: 'contact', label: 'Go to Contact', action: () => handleNav('contact') },
    { group: 'contact', label: 'Send an email', hint: '✉', action: () => handleNav('email') },
    { group: 'link', label: 'Open GitHub', action: () => open(GITHUB_URL) },
    { group: 'link', label: 'Open LinkedIn', action: () => open(LINKEDIN_URL) },
  ];

  return (
    <div className="app-shell">
      <div className="app-rail">
        <Sidebar
          items={items}
          value={active}
          onChange={handleNav}
          header={<span className="rail-logo">NLTW</span>}
          footer={
            <span className="rail-hint">
              <Kbd>⌘K</Kbd> to search
            </span>
          }
        />
      </div>

      <main className="app-main">
        <Hero />
        <About />
        <Experience />
        <Skills />
        <Education />
        <Activity github={github} />
        <Contact />
        <Footer />
      </main>

      <CommandPalette commands={commands} placeholder="Search or jump to…" />
      <ToastStack />
      <WebMcpTools />
    </div>
  );
}

export default App;
